import { type LucideIcon, Minus, TrendingDown, TrendingUp } from "lucide-react";
import type { ReactNode } from "react";

import type { MetricDelta, TrendDirection } from "@busright/data-contract";

import { formatSignedPct } from "@/lib/format";

import { Skeleton } from "./Skeleton";

export interface KpiCardProps {
  // Accepts a node so a <MetricLabel> (definition tooltip) can stand in for a plain string.
  readonly label: ReactNode;
  readonly value: ReactNode;
  readonly icon?: LucideIcon;
  // Source line (always shown — every KPI must declare provenance).
  readonly source: string;
  // Optional delta vs prior window. When the contract reports previous=null we show nothing.
  readonly delta?: MetricDelta;
  // Optional accent for the value (e.g. a RAG color for % critical). Defaults to primary text.
  readonly valueColor?: string;
  readonly loading?: boolean;
}

const DIRECTION_CLASS: Record<TrendDirection, string> = { UP: "up", DOWN: "down", FLAT: "flat" };
const directionClass = (d: TrendDirection): string => DIRECTION_CLASS[d];

const DirectionIcon = ({ direction }: { readonly direction: TrendDirection }): ReactNode => {
  if (direction === "UP") {
    return <TrendingUp size={13} aria-hidden />;
  }
  if (direction === "DOWN") {
    return <TrendingDown size={13} aria-hidden />;
  }
  return <Minus size={13} aria-hidden />;
};

// A single hero KPI. tabular-nums on the value; provenance + optional delta in the footer.
export const KpiCard = ({
  label,
  value,
  icon: Icon,
  source,
  delta,
  valueColor,
  loading,
}: KpiCardProps): ReactNode => {
  const hasDelta = delta && delta.previous !== null && delta.deltaPct !== null;
  return (
    <div className="kpi">
      <div className="kpi-label">
        {Icon ? <Icon className="icon" size={14} aria-hidden /> : null}
        {label}
      </div>
      {loading ? (
        <Skeleton height={30} width="60%" />
      ) : (
        <div
          className="kpi-value br-tabular-nums"
          style={valueColor ? { color: valueColor } : undefined}
        >
          {value}
        </div>
      )}
      <div className="kpi-foot">
        <span className="kpi-source">src: {source}</span>
        {hasDelta ? (
          <span className={`kpi-delta ${directionClass(delta.direction)}`}>
            <DirectionIcon direction={delta.direction} />
            <span className="br-tabular-nums">{formatSignedPct(delta.deltaPct)}</span>
          </span>
        ) : null}
      </div>
    </div>
  );
};
